import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, NonNullableFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { UserService } from '@coin/admin/core/data-access';
import { switchMapWithLoadingState } from '@coin/shared/core/util-helpers';
import { EmployeeLight } from '@coin/shared/core/util-models';
import { Subject, catchError, map } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ClickedOutsideDirective, TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import {
  V2SearchBoxComponent,
  V2ButtonComponent,
  V2DialogFrameComponent,
  V2DialogFooterComponent
} from '@coin/shared/core/ui-storybook';
import { EmployeePreviewComponent } from '@coin/shared/auth/feature';

/**
 * @deprecated Use the shared standalone variant
 * `SearchEmployeeDialogComponent` from `@coin/shared/employee-data/feature`
 * instead. The shared version is reusable across apps.
 */
@Component({
  selector: 'admin-search-employee-dialog',
  templateUrl: './search-employee-dialog.component.html',
  styleUrls: ['./search-employee-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    V2DialogFrameComponent,
    EmployeePreviewComponent,
    MatIcon,
    ClickedOutsideDirective,
    V2SearchBoxComponent,
    FormsModule,
    ReactiveFormsModule,
    TemplateWithValueDirective,
    V2DialogFooterComponent,
    V2ButtonComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class SearchEmployeeDialogComponent {
  dialogRef = inject<MatDialogRef<SearchEmployeeDialogComponent>>(MatDialogRef);
  private readonly userService = inject(UserService);
  private readonly formBuilder = inject(NonNullableFormBuilder);

  public formGroup = this.formBuilder.group({
    employee: new FormControl<EmployeeLight>(null, Validators.required)
  });

  public employeeSearch$ = new Subject<string>();
  public employees$ = this.employeeSearch$.pipe(
    switchMapWithLoadingState(search =>
      this.userService.queryUsersByGID(search).pipe(
        map(result => result.content),
        catchError(() => [])
      )
    )
  );

  public onRemoveButtonClick(): void {
    this.formGroup.controls.employee.setValue(null);
  }

  public confirm(): void {
    this.dialogRef.close(this.formGroup.getRawValue().employee);
  }
}
